import { useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';

import { confirmarLeituraFeedback, FeedbackPendente } from 'services/report.service';

interface Props {
  feedback: FeedbackPendente;
  onConfirmado: () => void;
}

interface Moldura {
  titulo: string;
  contexto: string;
}

/* Esta modal só é vista por quem sinalizou. Quem escreveu o reconhecimento
   não é avisado em nenhuma das duas decisões — é o que garante que ele não
   descubra quem o sinalizou, já que o reconhecimento tem um destinatário só
   e o aviso apontaria direto para essa pessoa. */
function molduraDe(feedback: FeedbackPendente): Moldura {
  if (feedback.decisao === 'approved') {
    return {
      titulo: 'Sua sinalização foi acolhida',
      contexto: 'O reconhecimento foi retirado de exibição em definitivo.',
    };
  }

  return {
    titulo: 'Sua sinalização foi avaliada',
    contexto: 'O reconhecimento está de acordo com as políticas do RECONHEÇA e volta a ser exibido.',
  };
}

export default function ModalFeedbackSinalizacao({ feedback, onConfirmado }: Props) {
  ReactModal.setAppElement('#modal');

  const [gravando, setGravando] = useState(false);
  const moldura = useMemo(() => molduraDe(feedback), [feedback]);

  /* Em modo somente leitura o interceptor (services/interceptors.ts) bloqueia
     sempre o POST database/read_notifications__c que essa modal usa para
     confirmar a leitura, e ela não tem outra saída (sem X, sem Esc, sem
     overlay clicável) — travaria o app inteiro. Mesma guarda do
     regulamento-modal (src/components/regulamento-modal/index.tsx): não
     exibir a modal quando readOnly está setado. */
  if (localStorage.getItem('readOnly')) {
    return null;
  }

  async function handleConfirmar() {
    if (gravando) return;
    setGravando(true);

    try {
      await confirmarLeituraFeedback(feedback._id);
      onConfirmado();
    } catch {
      /* Sem gravar, a modal não fecha. Fechar aqui faria a pessoa perder o
         parecer sem que o servidor soubesse que ela leu, e ele voltaria no
         próximo carregamento sem explicação. */
      toast.error('Não foi possível registrar sua confirmação. Tente novamente em alguns minutos.', {
        position: 'top-right',
        autoClose: 5000,
      });
      setGravando(false);
    }
  }

  return (
    <ReactModal
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      isOpen={true}
      contentLabel={moldura.titulo}
      portalClassName="relative z-50"
      overlayClassName="rb-modal-overlay rb-modal-overlay--top"
      contentElement={() => (
        <div className="rb-modal rb-modal--md">
          {/* Sem botão de fechar de propósito: a modal existe para ser lida,
              e só sai da frente por confirmação explícita. */}
          <div className="rb-modal__header">
            <h1 className="rb-modal__title">{moldura.titulo}</h1>
          </div>

          <p className="rb-modal__text">{moldura.contexto}</p>

          <div className="rb-modal__field">
            <span className="rb-modal__label">Avaliação da equipe</span>
            <p className="rb-modal__text" style={{ whiteSpace: 'pre-wrap' }}>
              {feedback.parecer}
            </p>
          </div>

          <div className="rb-modal__actions">
            <button className="rb-btn-primary" onClick={handleConfirmar} disabled={gravando}>
              Entendi
            </button>
          </div>
        </div>
      )}
    />
  );
}
